<template>
  <section>
    <!-- Translated pages: thank-you message here (the English site goes to
         the "quote request sent" page instead) -->
    <div v-if="isSent" class="modal-body sent">
      <div class="sent-icon" aria-hidden="true">✓</div>
      <p class="page-title mb-2">{{ $t("quote.sentTitle") }}</p>
      <p class="subtitle-text">{{ $t("quote.sentText", { email: clientEmail.trim() }) }}</p>
    </div>

    <div v-else class="modal-body">
      <p class="page-title mb-2">{{ $t("quote.title") }}</p>
      <p class="mb-5 subtitle-text">
        {{ intro || $t("quote.intro") }}
      </p>

      <b-field :label="$t('quote.name')" class="mb-4">
        <b-input required v-model="name"></b-input>
      </b-field>

      <b-field :label="$t('quote.email')" class="mb-4">
        <b-input required type="email" v-model="clientEmail"></b-input>
      </b-field>

      <b-field :label="$t('quote.phone')" class="mb-4">
        <vue-tel-input
          ref="phoneInput"
          class="input"
          :mode="'international'"
          :inputOptions="{
            showDialCode: true,
            inputmode: 'numeric',
            pattern: '[0-9]*',
            type: 'tel',
            placeholder: $t('quote.phonePlaceholder'),
          }"
          validCharactersOnly
          v-model="phoneNumber"
          :placeholder="$t('quote.phonePlaceholder')"
        />
      </b-field>

      <b-field :label="$t('quote.preferredDate')" class="mb-4" grouped>
        <b-button
          icon-left="calendar"
          class="calendar-icon mr-2"
          type="is-primary"
        ></b-button>
        <b-datepicker
          expanded
          ref="datepicker"
          v-model="date"
          :locale="dateLocale"
          :mobile-native="false"
          :min-date="minDate"
          :max-date="maxDate"
        >
          <template v-slot:trigger>
            <b-input
              @click="$refs.datepicker.toggle()"
              expanded
              readonly
              :value="humanizedDate"
            />
          </template>
        </b-datepicker>
      </b-field>

      <b-field :label="$t('quote.participants')" class="mb-4">
        <b-numberinput
          type="is-primary"
          v-model="participants"
          min="1"
          controls-alignment="right"
        ></b-numberinput>
      </b-field>

      <b-field :label="$t('quote.notes')" class="mb-5">
        <b-input
          type="textarea"
          v-model="notes"
          :placeholder="$t('quote.notesPlaceholder')"
        ></b-input>
      </b-field>

      <b-button
        type="is-primary"
        expanded
        :loading="isSendingRequest"
        :disabled="!isFormValid"
        @click="sendRequest"
      >
        {{ $t("quote.submit") }}
      </b-button>

      <i18n path="quote.noPayment" tag="p" class="info mt-5">
        <template #email>
          <a class="link" href="mailto:support@guiders.pt">{{ $t("quote.emailUs") }}</a>
        </template>
        <template #whatsapp>
          <a class="link" href="https://wa.me/351920043375" target="_blank">{{ $t("quote.whatsapp") }}</a>
        </template>
      </i18n>
    </div>
  </section>
</template>

<script>
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { DEFAULT_LOCALE, currentLocaleInfo } from "@/i18n";

export default {
  // intro: optional text under the title (defaults to the tour wording)
  props: ["tourData", "fullName", "email", "intro"],
  data() {
    const today = new Date();
    return {
      name: this.fullName || "",
      clientEmail: this.email || "",
      phoneNumber: "",
      date: null,
      participants: 2,
      notes: "",
      isSendingRequest: false,
      isSent: false,
      minDate: new Date(today.getTime() + 24 * 60 * 60 * 1000),
      maxDate: new Date(
        today.getFullYear() + 1,
        today.getMonth(),
        today.getDate(),
      ),
    };
  },
  computed: {
    dateLocale() {
      return currentLocaleInfo().dateLocale;
    },
    humanizedDate() {
      return this.date
        ? this.date.toLocaleDateString(this.dateLocale, { day: "numeric", month: "long", year: "numeric" })
        : "";
    },
    isFormValid() {
      return (
        this.name.trim().length > 0 &&
        /\S+@\S+\.\S+/.test(this.clientEmail) &&
        this.isValidPhoneNumber(this.phoneNumber) &&
        !!this.date
      );
    },
  },
  mounted() {
    // vue-tel-input doesn't forward inputmode, so set it on the real input
    // to get a digits-only keyboard on phones.
    this.$refs.phoneInput?.$refs.input?.setAttribute("inputmode", "numeric");
  },
  methods: {
    isValidPhoneNumber(number) {
      const phoneNumber = parsePhoneNumberFromString(number || "");
      return phoneNumber ? phoneNumber.isValid() : false;
    },
    async sendRequest() {
      const tourUrl = `https://guiders.pt${this.$route.fullPath}`;
      const content = [
        "New quote request",
        "",
        `Tour: ${this.tourData.title}`,
        `Link: ${tourUrl}`,
        "",
        `Preferred date: ${this.date.toDateString()}`,
        `Participants: ${this.participants}`,
        "",
        `Name: ${this.name}`,
        `Email: ${this.clientEmail}`,
        `Phone: ${this.phoneNumber}`,
        "",
        "Notes:",
        this.notes.trim() || "-",
        "",
        // So the team can reply in the visitor's language
        `Website language: ${currentLocaleInfo().name}`,
      ].join("\n");
      const data = {
        subject: `Quote request: ${this.tourData.title} (${this.name})`,
        content,
      };

      this.isSendingRequest = true;
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/send/mail`,
          {
            method: "POST",
            body: JSON.stringify(data),
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
          },
        );
        const result = await response.json();
        if (!result.success) throw new Error();
        if (this.$i18n.locale !== DEFAULT_LOCALE) {
          this.isSent = true;
          return;
        }
        window.scrollTo(0, 0);
        this.$router.push({ name: "quote-request-sent" });
      } catch (error) {
        this.$buefy.toast.open({
          duration: 10000,
          message: this.$t("quote.error"),
          position: "is-bottom",
          type: "is-danger",
        });
      } finally {
        this.isSendingRequest = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.sent {
  text-align: center;
  padding: 2rem 1.5rem;
}

.sent-icon {
  width: 3.5rem;
  height: 3.5rem;
  margin: 0 auto 1rem;
  border-radius: 999px;
  background: #f2f6f2;
  color: #14342b;
  font-size: 1.5rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  border-radius: 8px;
  padding: 20px;
  background-color: white;
  width: -webkit-fill-available;
  min-height: auto;
  border: 1px solid #edebf2;
}

.page-title {
  font-size: 22px;
}

.subtitle-text {
  color: #45505f;
  font-size: 14px;
}

.calendar-icon {
  border-radius: 8px;
}

.info {
  padding: 12px 16px;
  border-radius: 8px;
  background-color: #f6fbff;
  font-size: 14px;
  font-weight: 500;
}
</style>
